import CopilotPlugins from 'copilot-plugins/plugins';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectGates } from '../auth/selectors/authSelectors';
import { selectMessages as v1SelectMessages } from '../chat/selectors/chatSelectors';
import { selectMessages as v2SelectMessages } from '../chat/selectors/chatv2Selectors';
import { selectParentAppName, selectPlugins } from '../chat/selectors/parentAppSelectors';
import { resetParentAppState, setIsWaitingForExtraIntentProperties, updateExtraIntentProperties, updateParentAppState } from '../chat/slices/parentApp';
import { transformCopilotV2ApiMessagesToV1Messages } from '../chat/utils/copilotV2ResponseUtil';
import { useIsUngatedForCopilotV2 } from '../gating/hooks/useIsUngatedForCopilotV2';
import { EDITOR_PLUGIN } from '../types/PluginTypes';
import { updateEditorPluginText } from '../utils/collaborationUtils';
import { useCollaborateCopilotActions } from './useCollaborateCopilotActions';
export const useUpdateCollaborationParentState = () => {
  const dispatch = useDispatch();
  return {
    updateParentState: useCallback(action => {
      dispatch(updateParentAppState(action.payload));
    }, [dispatch]),
    updateExtraIntentProperties: useCallback(action => {
      dispatch(updateExtraIntentProperties(action));
    }, [dispatch]),
    resetParentAppState: useCallback(() => {
      dispatch(resetParentAppState());
    }, [dispatch])
  };
};
export const useCollaborationParentState = () => {
  const gates = useSelector(selectGates);
  const sessionPlugins = useSelector(selectPlugins);
  const parentAppName = useSelector(selectParentAppName);
  const sessionPluginNames = useMemo(() => {
    return Object.keys(sessionPlugins);
  }, [sessionPlugins]);
  const allowedPlugins = useMemo(() => sessionPluginNames.filter(pluginName => {
    const plugin = CopilotPlugins[pluginName];
    if (!plugin) {
      return false;
    }
    const hasGate = gates && gates.includes(plugin.gate);
    return Boolean(parentAppName) && hasGate;
  }), [gates, parentAppName, sessionPluginNames]);
  const getParentAppStateByPlugin = useCallback(pluginName => {
    var _sessionPlugins$plugi;
    return (_sessionPlugins$plugi = sessionPlugins[pluginName]) === null || _sessionPlugins$plugi === void 0 ? void 0 : _sessionPlugins$plugi.parentAppState;
  }, [sessionPlugins]);
  const getParentAppSessionIdByPlugin = useCallback(pluginName => {
    var _sessionPlugins$plugi2;
    return (_sessionPlugins$plugi2 = sessionPlugins[pluginName]) === null || _sessionPlugins$plugi2 === void 0 ? void 0 : _sessionPlugins$plugi2.sessionId;
  }, [sessionPlugins]);
  const getAllowedPluginsByIntent = useCallback(intent => allowedPlugins.filter(pluginName => {
    const plugin = CopilotPlugins[pluginName];
    if (!plugin) return false;
    const hasIntent = intent && plugin.intentTypes.includes(intent);
    return hasIntent;
  }), [allowedPlugins]);
  return {
    sessionPluginNames,
    allowedPlugins,
    getParentAppStateByPlugin,
    getParentAppSessionIdByPlugin,
    getAllowedPluginsByIntent
  };
};
export const useWaitForExtraIntentProperties = () => {
  const dispatch = useDispatch();

  // TODO: remove this once we drop chatv1.
  const isUngatedForCopilotV2 = useIsUngatedForCopilotV2();
  const v1Messages = useSelector(v1SelectMessages);
  const v2Messages = useSelector(v2SelectMessages);
  const messages = isUngatedForCopilotV2 ? transformCopilotV2ApiMessagesToV1Messages(v2Messages) : v1Messages;
  const sessionPlugins = useSelector(selectPlugins);
  const talkingAboutText = useSelector(state => {
    var _state$parentApp$plug, _state$parentApp$plug2;
    return (_state$parentApp$plug = (_state$parentApp$plug2 = state.parentApp.plugins[EDITOR_PLUGIN]) === null || _state$parentApp$plug2 === void 0 ? void 0 : _state$parentApp$plug2.talkingAboutText) !== null && _state$parentApp$plug !== void 0 ? _state$parentApp$plug : null;
  });
  const [hasStartedFetching, setHasStartedFetching] = useState(false);
  const resolveRef = useRef(__value => {});
  const {
    requestContextFromSession
  } = useCollaborateCopilotActions();
  const {
    allowedPlugins,
    getParentAppSessionIdByPlugin
  } = useCollaborationParentState();
  const activePluginsStateInfoAndState = allowedPlugins.map(pluginName => sessionPlugins[pluginName]);
  const isWaitingForExtraIntentProperties = useMemo(() => activePluginsStateInfoAndState.some(plugin => plugin.waitingForExtraIntentProperties), [activePluginsStateInfoAndState]);
  const updateEditorText = useCallback(text => {
    dispatch(updateParentAppState({
      plugin: EDITOR_PLUGIN,
      pluginProps: {
        talkingAboutText: text
      },
      sessionId: getParentAppSessionIdByPlugin(EDITOR_PLUGIN)
    }));
  }, [dispatch, getParentAppSessionIdByPlugin]);
  const fetchExtraIntentProperties = useCallback(() => {
    const pluginSessionsIds = allowedPlugins.map(pluginName => getParentAppSessionIdByPlugin(pluginName))
    // Only allow unique cases, so we don't send twice the same request
    .filter((sessionId, index, self) => self.indexOf(sessionId) === index);

    // Set the waiting state for all plugins
    dispatch(setIsWaitingForExtraIntentProperties());
    // Request extra intent properties for all plugins
    pluginSessionsIds.forEach(sessionId => {
      requestContextFromSession({
        requestSessionsId: sessionId
      });
    });
    setHasStartedFetching(true);
  }, [allowedPlugins, dispatch, getParentAppSessionIdByPlugin, requestContextFromSession]);
  const getCombinedExtraIntentProperties = useCallback(() => {
    const combinedResults = allowedPlugins.reduce((acc, pluginName) => {
      const plugin = sessionPlugins[pluginName];
      if (!plugin.waitingForExtraIntentProperties && plugin.extraIntentProperties) {
        // For editor plugin, update the text to last message before combining
        if (pluginName === EDITOR_PLUGIN) {
          const updatedProperties = updateEditorPluginText(talkingAboutText, updateEditorText, messages, plugin.extraIntentProperties);
          return Object.assign(acc, updatedProperties);
        }
        return Object.assign(acc, plugin.extraIntentProperties || {});
      }
      return acc;
    }, {});
    return Object.keys(combinedResults).length > 0 ? combinedResults : undefined;
  }, [allowedPlugins, messages, sessionPlugins, talkingAboutText, updateEditorText]);
  const resolveExtraIntentProperties = useCallback(() => {
    const combinedExtraIntentProperties = getCombinedExtraIntentProperties();
    setHasStartedFetching(false);
    resolveRef.current(combinedExtraIntentProperties);
  }, [getCombinedExtraIntentProperties]);

  // Listen for changes in the plugins state
  useEffect(() => {
    if (!hasStartedFetching) {
      return;
    }
    const timer = setTimeout(resolveExtraIntentProperties, 250);
    if (!isWaitingForExtraIntentProperties) {
      resolveExtraIntentProperties();
      clearTimeout(timer);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [getCombinedExtraIntentProperties, hasStartedFetching, isWaitingForExtraIntentProperties, resolveExtraIntentProperties]);

  // If there are no plugins, we don't need to wait for extra intent properties
  if (allowedPlugins.length === 0) {
    return () => Promise.resolve(undefined);
  }
  return () => {
    fetchExtraIntentProperties();
    return new Promise(resolve => {
      resolveRef.current = resolve;
    });
  };
};